import { Navigate, Route, Routes } from 'react-router-dom';
import { AlertTriangle, Info } from 'lucide-react';
import { isConfigured } from '@/lib/supabase';
import { useAuth, useMunicipal } from '@/lib/session';
import { Layout } from '@/components/Layout';
import { Loading } from '@/components/shell';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Login, Shell } from '@/screens/Login';
import { Stato } from '@/screens/Stato';
import { Nuovo } from '@/screens/Nuovo';
import { Storico } from '@/screens/Storico';
import { Dettaglio } from '@/screens/Dettaglio';

/**
 * I quattro stati prima di poter disegnare qualcosa, nell'ordine in cui vanno
 * esclusi. Ognuno ha una schermata sua, perché ognuno ha una cura diversa e
 * confonderli manda l'operatore a chiamare la persona sbagliata:
 *
 *   1. console non configurata → è un problema di deploy, non suo;
 *   2. sessione in caricamento  → aspetti un attimo;
 *   3. non autenticato          → login;
 *   4. autenticato ma non abilitato → non è un errore, è un fatto.
 */
export function App() {
  const { userId, loading } = useAuth();
  const municipal = useMunicipal();

  if (!isConfigured) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Console non configurata</AlertTitle>
          <AlertDescription>
            Mancano l’indirizzo di Supabase o la chiave pubblica. È un problema
            di configurazione del deploy, non del tuo accesso: segnalalo a chi
            gestisce il servizio.
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  if (loading) return <Loading what="Apro la console" />;
  if (!userId) return <Login />;

  if (municipal.isLoading) return <Loading what="Carico il tuo Comune" />;

  if (municipal.isError) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Non riesco a leggere il tuo profilo</AlertTitle>
          <AlertDescription>
            {municipal.error instanceof Error
              ? municipal.error.message
              : 'Riprova fra poco.'}
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const session = municipal.data;
  if (!session) {
    return (
      <Shell>
        <Alert>
          <Info />
          <AlertTitle>Account non abilitato</AlertTitle>
          <AlertDescription>
            Questo account non è abilitato a pubblicare per nessun Comune.
            L’abilitazione si fa a mano, dopo la convenzione: se dovrebbe
            esserci, scrivi a chi ti ha dato l’accesso.
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  return (
    <Routes>
      <Route element={<Layout session={session} />}>
        <Route path="/" element={<Stato session={session} />} />
        <Route path="/nuovo" element={<Nuovo session={session} />} />
        <Route path="/storico" element={<Storico session={session} />} />
        <Route path="/avviso/:id" element={<Dettaglio session={session} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
